import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export default function OverUnderMethodPage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);

    const siteUrl = 'https://overunderroofing.com';
    const existing = document.getElementById('overunder-method-schema');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = 'overunder-method-schema';
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: 'The OverUnder Method™ – How to Choose the Right Roofing Bid',
      description:
        'A structured, 5-step method for evaluating roofing bids so Wake County homeowners can confidently choose the best value — not just the lowest price.',
      url: `${siteUrl}/overunder-method`,
      estimatedCost: { '@type': 'MonetaryAmount', currency: 'USD', value: '500' },
      step: [
        { '@type': 'HowToStep', position: 1, name: 'Normalize the Bids', text: 'Adjust each bid to reflect equivalent materials and scope for a true apples-to-apples comparison.' },
        { '@type': 'HowToStep', position: 2, name: 'Identify Hidden Gaps', text: 'Surface missing components like underlayment quality, flashing, and ventilation before you decide.' },
        { '@type': 'HowToStep', position: 3, name: 'Map Price vs. Quality', text: 'Evaluate each bid across both cost and quality — materials, scope, and contractor reliability.' },
        { '@type': 'HowToStep', position: 4, name: 'Find the Value Zone', text: 'Identify the bid where quality is strong, scope is complete, and price is fair.' },
        { '@type': 'HowToStep', position: 5, name: 'Negotiate from Leverage', text: 'Use the full bid landscape to improve pricing, upgrade materials, and strengthen warranties.' },
      ],
    });
    document.head.appendChild(script);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      const el = document.getElementById('overunder-method-schema');
      if (el) el.remove();
    };
  }, []);

  const steps = [
    {
      number: '01',
      title: 'Normalize the Bids',
      intro: 'Before you compare prices, you need to make sure you\'re comparing the same scope of work.',
      body: 'Most bids differ in:',
      bullets: [
        'Materials (shingle quality, underlayment, ventilation)',
        'Scope (what\'s included vs omitted)',
        'Assumptions (repairs, disposal, flashing, etc.)',
      ],
      close: 'We adjust each bid to reflect equivalent materials and scope—so you\'re making an apples-to-apples comparison.',
      callout: 'Without this step, price comparisons are misleading.',
    },
    {
      number: '02',
      title: 'Identify Hidden Gaps',
      intro: 'Many roofing quotes leave out critical components that only show up later as change orders or shortcuts.',
      body: 'Common gaps include:',
      bullets: [
        'Underlayment quality',
        'Flashing replacement',
        'Ventilation upgrades',
        'Warranty differences',
        'Cleanup and disposal details',
      ],
      close: 'These omissions can make a bid look cheaper—but lead to higher costs or lower quality later.',
      callout: 'We surface these gaps before you make a decision.',
    },
    {
      number: '03',
      title: 'Map Price vs. Quality',
      intro: 'Most homeowners compare bids based on price alone. But price without context doesn\'t tell you much.',
      body: 'We evaluate each bid across both:',
      bullets: [
        'Cost',
        'Quality (materials, scope, contractor reliability)',
      ],
      close: 'This creates a clearer picture of what you\'re actually getting for your money.',
      callout: 'Not all $12,000 roofs are the same.',
    },
    {
      number: '04',
      title: 'Find the "Value Zone"',
      intro: 'The best option is rarely the cheapest—or the most expensive.',
      body: 'Instead, it\'s the bid where:',
      bullets: [
        'Quality is strong',
        'Scope is complete',
        'Price is fair relative to what\'s included',
      ],
      close: 'We call this the Value Zone—where cost and quality are properly aligned.',
      callout: 'This is where smart decisions happen.',
    },
    {
      number: '05',
      title: 'Negotiate from Leverage',
      intro: 'Once you understand the full landscape of bids, you\'re no longer guessing—you have leverage.',
      body: 'We use that leverage to:',
      bullets: [
        'Improve pricing',
        'Upgrade materials',
        'Clarify scope',
        'Strengthen warranties',
      ],
      close: 'Contractors are more responsive when they know their bids are being compared carefully.',
      callout: 'Better information leads to better outcomes.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <title>The OverUnder Method™ – How to Choose the Right Roofing Bid | OverUnder Roofing</title>
      <meta name="description" content="The OverUnder Method™ is a structured 5-step process for evaluating roofing bids in Wake County NC. Compare bids properly, find hidden gaps, and choose the best value — not just the lowest price." />
      <link rel="canonical" href="https://overunderroofing.com/overunder-method" />

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg' : 'bg-white/95 shadow-sm'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <img
              src="https://public.readdy.ai/ai/img_res/82f8a00e-bc28-4af6-bf3f-740e76e3ab6b.png"
              alt="OverUnder Roofing Logo – Independent Roofing Advocate Wake County NC"
              className="h-10 w-auto"
            />
            <div>
              <p className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                OverUnder Roofing
              </p>
              <p className="text-xs italic text-gray-500" style={{ fontFamily: 'Playfair Display, serif' }}>
                The House Always Wins
              </p>
            </div>
          </Link>
          <Link to="/" className="text-sm font-medium text-gray-600 hover:text-emerald-700 transition-colors whitespace-nowrap cursor-pointer">
            &larr; Back to Home
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-14 bg-gradient-to-b from-slate-50 to-white border-b border-slate-100">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-emerald-100 px-4 py-2 rounded-full mb-6">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-award-line text-emerald-700 text-sm"></i>
            </div>
            <span className="text-sm font-semibold text-emerald-700">Our Proprietary Process</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            The OverUnder Method™
          </h1>
          <p className="text-xl text-gray-500 mb-0 leading-relaxed">
            How to Choose the Right Roofing Bid (Without Guessing)
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-14 bg-white">
        <div className="max-w-2xl mx-auto px-6">
          <p className="text-lg text-gray-700 leading-relaxed mb-5">
            Most homeowners are told to "get 2–3 quotes and pick one."
            Unfortunately, that approach often leads to overpaying, missed details, or poor workmanship.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed mb-5">
            Why? Because roofing bids are rarely comparable at face value.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            The <strong>OverUnder Method™</strong> is a simple, structured way to evaluate roofing quotes so you can confidently choose the best value—not just the lowest price.
          </p>
        </div>
      </section>

      <div className="border-t border-slate-100" />

      {/* Steps */}
      <section className="py-14 bg-white">
        <div className="max-w-2xl mx-auto px-6 space-y-0">
          {steps.map((step, i) => (
            <div key={i}>
              <div className="py-12">
                {/* Step header */}
                <div className="flex items-center gap-4 mb-6">
                  <span className="text-4xl font-bold text-slate-100 select-none" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {step.number}
                  </span>
                  <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Step {parseInt(step.number, 10)}: {step.title}
                  </h2>
                </div>

                <p className="text-base text-gray-700 leading-relaxed mb-4">{step.intro}</p>
                <p className="text-base text-gray-700 leading-relaxed mb-3">{step.body}</p>

                <ul className="space-y-2 mb-5 pl-1">
                  {step.bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-3">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-emerald-600 flex-shrink-0"></span>
                      <span className="text-base text-gray-700">{b}</span>
                    </li>
                  ))}
                </ul>

                <p className="text-base text-gray-700 leading-relaxed mb-5">{step.close}</p>

                <div className="border-l-4 border-emerald-500 pl-4">
                  <p className="text-base font-bold text-gray-900">{step.callout}</p>
                </div>
              </div>
              {i < steps.length - 1 && <div className="border-t border-slate-100" />}
            </div>
          ))}
        </div>
      </section>

      <div className="border-t border-slate-100" />

      {/* Why This Method Works */}
      <section className="py-14 bg-slate-50/60">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Why This Method Works
          </h2>
          <p className="text-base text-gray-700 leading-relaxed mb-5">
            Most roofing decisions go wrong because homeowners are forced to:
          </p>
          <ul className="space-y-2 mb-7 pl-1">
            {[
              'Compare inconsistent bids',
              'Interpret technical details',
              'Make a high-cost decision without clear data',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-emerald-600 flex-shrink-0"></span>
                <span className="text-base text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
          <p className="text-base text-gray-700 leading-relaxed mb-4">
            The OverUnder Method™ removes that uncertainty by creating:
          </p>
          <ul className="space-y-2 pl-1">
            {[
              'Clear comparisons',
              'Transparent tradeoffs',
              'Confident decisions',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-emerald-600 flex-shrink-0"></span>
                <span className="text-base text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <div className="border-t border-slate-100" />

      {/* Bottom Line */}
      <section className="py-14 bg-white">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-5" style={{ fontFamily: 'Playfair Display, serif' }}>
            The Bottom Line
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-2">
            Don't pick the lowest bid.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Don't assume the highest is best.
          </p>
          <p className="text-lg font-bold text-gray-900 leading-relaxed">
            Use a structured approach to find the best value.
          </p>
        </div>
      </section>

      <div className="border-t border-slate-100" />

      {/* CTA */}
      <section className="py-20 bg-emerald-800">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Want Help Applying This Method?
          </h2>
          <p className="text-emerald-200 text-lg mb-3 leading-relaxed">
            That's exactly what we do.
          </p>
          <p className="text-emerald-200 text-base mb-10 leading-relaxed">
            OverUnder Roofing acts as your independent advocate—managing the bidding process, analyzing the details, and helping you choose with confidence.
          </p>
          <Link
            to="/#contact"
            className="inline-block bg-orange-500 hover:bg-orange-600 text-white px-10 py-4 rounded-2xl font-semibold text-base transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            Get Started &rarr;
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-sm text-gray-400">© 2025 OverUnder Roofing. All rights reserved.</p>
          <div className="flex items-center gap-6 text-sm text-gray-400">
            <Link to="/privacy" className="hover:text-emerald-400 transition-colors cursor-pointer">Privacy Policy</Link>
            <span>|</span>
            <Link to="/terms" className="hover:text-emerald-400 transition-colors cursor-pointer">Terms of Service</Link>
            <span>|</span>
            <Link to="/accessibility" className="hover:text-emerald-400 transition-colors cursor-pointer">Accessibility</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
